import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Button, Card, Table, Tag, Tabs, Input, Select, Space, Row, Col,
    Typography, Tooltip, Badge, DatePicker, Dropdown
} from "antd";
import dayjs from "dayjs";
import {
    FileAddOutlined, ReloadOutlined, RiseOutlined, FallOutlined,
    PauseCircleOutlined, AppstoreOutlined, SearchOutlined, LeftOutlined, SafetyCertificateOutlined,
    RightOutlined, PlusOutlined, DownloadOutlined, CalendarOutlined,
    EyeOutlined, BankOutlined,
    FilePdfOutlined, DownOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import * as XLSX from "xlsx";
import { frenchDateOnly } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";
import { COLORS } from "../../components/ui/colors";
import { StatCard } from "../../components/statistics/StatCard";
import { LOGGED_USER } from "../../variables/constants";
import { searchProduction } from "../../hooks/useSearch";
import { renderContratSearchResults } from "./components/ContratSearchResults";
import { generateContratsProductionPDF, generateBordereauCessionPDF } from "./etatsProductionFunctions";
import { moisPeriodeLabel } from "../operations/cessions/bordereaux/bordereauExcel";
import { generateSinistresDeclaresPDF, generateSinistresDeclaresExcel } from "../stats/sinistre/etatsSinistresFunctions";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Onglets ↔ code avenant (filtre serveur). `kind` : "contrat" (listing standard via /api/contrat),
// "horspool" (contrats avec garanties hors Pool → /api/contrat/hors-pool),
// "sinistres" (état des sinistres → /api/sinistre/sql).
const TABS = [
    { key: "6", label: "Tous", kind: "contrat", avenant: null, icon: <AppstoreOutlined />, countKey: null, color: COLORS.secondary },
    { key: "1", label: "Affaires nouvelles", kind: "contrat", avenant: "AN", icon: <FileAddOutlined />, countKey: "avenantANCount", color: "#52c41a" },
    // Renouvellements + avenants positifs fusionnés : le filtre "P" (prime nette > 0, hors AN)
    // inclut déjà les renouvellements. La colonne « Avenant » précise le type sur chaque ligne.
    { key: "3", label: "Renouvellements & avenants positifs", kind: "contrat", avenant: "P", icon: <RiseOutlined />, countKey: "primePositiveCount", color: "#52c41a" },
    { key: "4", label: "Avenants négatifs", kind: "contrat", avenant: "N", icon: <FallOutlined />, countKey: "primeNegativeCount", color: "#ff4d4f" },
    { key: "5", label: "Suspensions", kind: "contrat", avenant: "SP", icon: <PauseCircleOutlined />, countKey: "avenantSPCount", color: "#faad14" },
    // Contrats dont AU MOINS UN engin porte une garantie créée par une COMPAGNIE
    // (hors Pool) — une ligne par contrat, via /api/contrat/hors-pool.
    { key: "7", label: "Garanties hors Pool", kind: "horspool", avenant: null, icon: <SafetyCertificateOutlined />, countKey: null, color: "#7c3aed" },
    // Contrats souscrits pour l'ÉTAT (HCO106) : validés, imprimés sans encaissement, en attente de
    // paiement (6 mois). Source dédiée /api/contrat/etat. Quittent la liste dès l'encaissement.
    { key: "8", label: "Contrats avec l'État", kind: "etat", avenant: null, icon: <BankOutlined />, countKey: null, color: "#b45309" },
    // Onglet « Sinistres » retiré de /production/v2.
];

// Libellé d'état d'un sinistre (code entier renvoyé par /api/sinistre/sql).
const SINISTRE_ETAT = {
    0: { text: "À valider", color: "gold" }, 1: { text: "En instance", color: "green" },
    2: { text: "Ouvert", color: "blue" }, 3: { text: "Terminé", color: "geekblue" },
    4: { text: "Sans suite", color: "default" }, 5: { text: "Réouvert", color: "purple" },
    8: { text: "Refusé", color: "red" },
};

// Couleur du tag d'avenant selon le code.
const AVENANT_COLORS = {
    AN: "green", RN: "blue", PR: "cyan", IN: "geekblue", AG: "green",
    DG: "orange", RT: "volcano", SP: "gold", RV: "lime", RS: "red",
    RE: "purple", TG: "magenta", CV: "blue", AO: "default", OE: "default", AA: "red",
};

const CURRENT_YEAR = new Date().getFullYear();
const EXERCICES = Array.from({ length: CURRENT_YEAR - 2023 + 1 }, (_, i) => 2024 + i); // 2024 → année courante

const isPoolSiege =
    LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" &&
    (LOGGED_USER?.agence?.typeAgence === "SIEGE" || !LOGGED_USER?.agence);

// Compagnie d'assurance au SIÈGE → voit toute sa compagnie ; peut filtrer par agence.
const isCompagnieSiege =
    LOGGED_USER?.profil?.organisme?.typeOrganisme === "ASSURANCE" &&
    (LOGGED_USER?.agence?.typeAgence === "SIEGE" || !LOGGED_USER?.agence);

// Courtier : ne voit que SES contrats (cf. backend), mais peut les filtrer PAR COMPAGNIE
// (il produit pour plusieurs compagnies via ses agréments).
const isCourtier = LOGGED_USER?.agence?.typeAgence === "COURTIER";

const COMPAGNIE_CODE = LOGGED_USER?.profil?.organisme?.code;

const ProductionV2 = ({ exclurePropositions = false } = {}) => {
    const navigate = useNavigate();

    // Filtres — INITIALISÉS depuis sessionStorage (init paresseuse → restaurés AVANT le 1er
    // rendu, pas de course ni de double-fetch). Sauvegarde via l'effet plus bas.
    const SAVED_FILTERS = useMemo(() => {
        try { return JSON.parse(sessionStorage.getItem("filters:production:v2") || "null") || {}; }
        catch (e) { return {}; }
    }, []);
    // Onglet « sinistres » supprimé : si un filtre persistant le pointe encore,
    // on retombe sur « Tous » (6).
    const [activeTab, setActiveTab] = useState(() => {
        const t = SAVED_FILTERS.activeTab ?? "6";
        return t === "sinistres" ? "6" : t;
    });
    // Type de l'onglet courant : "contrat" | "horspool" | "sinistres" — pilote data source, colonnes et exports.
    const tabKind = useMemo(() => TABS.find((t) => t.key === activeTab)?.kind || "contrat", [activeTab]);
    const [exercice, setExercice] = useState(SAVED_FILTERS.exercice ?? CURRENT_YEAR);   // année ou "ALL"
    const [customRange, setCustomRange] = useState(
        Array.isArray(SAVED_FILTERS.customRange) && SAVED_FILTERS.customRange.some(Boolean)
            ? SAVED_FILTERS.customRange.map((x) => (x ? dayjs(x) : null))
            : null);     // [dayjs, dayjs] période précise
    // date_effet | created_or_updated (créé OU mis à jour) — défaut. (created_at/updated_at restent acceptés côté serveur)
    const [dateField, setDateField] = useState(SAVED_FILTERS.dateField ?? "created_or_updated");
    const [search, setSearch] = useState(SAVED_FILTERS.search ?? "");
    const [debouncedSearch, setDebouncedSearch] = useState(SAVED_FILTERS.search ?? "");
    const [compagnies, setCompagnies] = useState(Array.isArray(SAVED_FILTERS.compagnies) ? SAVED_FILTERS.compagnies : []);
    const [poolAgences, setPoolAgences] = useState([]);
    const [compagnieAgences, setCompagnieAgences] = useState([]); // agences de la compagnie connectée
    // Filtre par BRANCHE. Les branches sont chargées depuis /api/branche, DÉJÀ filtrées côté serveur
    // par la visibilité (BrancheVisibilityUtils) → une compagnie n'y voit pas les branches gérées par
    // le Pool (VT/GUM). On envoie le codeBranche sélectionné au backend (param `branche`).
    const [branche, setBranche] = useState(SAVED_FILTERS.branche ?? null);
    const [branches, setBranches] = useState([]);

    // Données
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(SAVED_FILTERS.page ?? 1);
    const [pageSize, setPageSize] = useState(SAVED_FILTERS.pageSize ?? 10);
    const [total, setTotal] = useState(0);
    const [count, setCount] = useState({});
    const [membres, setMembres] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);

    const reqId = useRef(0);

    // Recherche de la NavBar (autocomplétion), identique à /production : dropdown de
    // contrats correspondants → clic = ouverture des détails.
    const [searchValue, setSearchValue] = useState("");
    const [searchResults, setSearchResults] = useState([]);

    // Persistance des filtres : sauvegarde à chaque changement (restauration via l'init
    // paresseuse ci-dessus). dayjs → ISO. Survit au retour depuis le détail + reload.
    useEffect(() => {
        try {
            sessionStorage.setItem("filters:production:v2", JSON.stringify({
                activeTab, exercice, dateField, search, compagnies, page, pageSize, branche,
                customRange: (customRange || []).map((d) => (d && d.toISOString ? d.toISOString() : null)),
            }));
        } catch (e) { /* quota / sérialisation */ }
    }, [activeTab, exercice, dateField, search, compagnies, page, pageSize, customRange, branche]);

    // Branches visibles pour l'organisme connecté (le backend exclut déjà les branches Pool
    // pour une compagnie). Alimente le Select de filtre par branche.
    useEffect(() => {
        axiosInstance.get("/api/branche")
            .then((r) => setBranches(Array.isArray(r.data) ? r.data : []))
            .catch(() => setBranches([]));
    }, []);

    const renderSearchResults = (results) => renderContratSearchResults(results, searchValue, navigate);

    useEffect(() => {
        if (!searchValue || searchValue.length <= 2) {
            setSearchResults([]);
            return;
        }
        const handler = setTimeout(() => {
            searchProduction(searchValue)
                .then((res) => setSearchResults(res.data.content))
                .catch((err) => console.log(err));
        }, 400);
        return () => clearTimeout(handler);
    }, [searchValue]);

    // Debounce recherche
    const searchMountedRef = useRef(false);
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search.trim());
            // Ne PAS réinitialiser la page au montage : préserve la page restaurée des filtres.
            if (searchMountedRef.current) setPage(1);
            searchMountedRef.current = true;
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    // Bornes de période → la période précise (RangePicker) prime sur l'exercice
    const dateRange = useMemo(() => {
        if (customRange && customRange[0] && customRange[1]) {
            return {
                startDate: customRange[0].format("YYYY-MM-DD"),
                endDate: customRange[1].format("YYYY-MM-DD"),
            };
        }
        if (exercice === "ALL") return null;
        return { startDate: `${exercice}-01-01`, endDate: `${exercice}-12-31` };
    }, [exercice, customRange]);

    // Fetch consolidé (une seule source de vérité). Branche selon le type d'onglet :
    // sinistres → /api/sinistre/sql ; hors Pool → /api/contrat/hors-pool ; sinon /api/contrat.
    const fetchData = useCallback(async () => {
        const myId = ++reqId.current;
        setLoading(true);
        try {
            const tab = TABS.find((t) => t.key === activeTab);
            const tabKind = tab?.kind || "contrat";

            // ----- Onglet SINISTRES -----
            if (tabKind === "sinistres") {
                const params = {
                    page: page - 1,
                    size: pageSize,
                    ...(debouncedSearch ? { search: debouncedSearch } : {}),
                    ...(compagnies.length ? { compagnie: compagnies.join(",") } : {}),
                    ...(dateRange ? { ...dateRange } : {}),
                };
                const res = await axiosInstance.get("/api/sinistre/sql", { params });
                if (myId !== reqId.current) return;
                const { content = [], totalElements = 0 } = res.data || {};
                setData(content.map((s, idx) => ({
                    key: s.codeId,
                    codeId: s.codeId,
                    idx,
                    numeroSinistre: s?.numeroSinistrem || s?.numSinistre || "",
                    numeroPolice: s?.numeroPoliceM || s?.numeroPoliceP || "",
                    souscripteur: s?.prenom ? `${s?.nom} ${s?.prenom}` : `${s?.nom || ""}`,
                    organisme: s?.organisme,
                    agence: s?.agence,
                    immatriculation: s?.immatriculation,
                    dateSinistre: s?.dateSinistre ? frenchDateOnly(s.dateSinistre) : "",
                    dateDeclaration: s?.dateDeclaration ? frenchDateOnly(s.dateDeclaration) : "",
                    etat: s?.etat,
                    evaluation: s?.evaluation,
                    data: s,
                })));
                setTotal(totalElements);
                return;
            }

            // ----- Onglet CONTRATS AVEC L'ÉTAT (HCO106) -----
            if (tabKind === "etat") {
                const params = { ...(compagnies.length ? { compagnie: compagnies.join(",") } : {}) };
                const res = await axiosInstance.get("/api/contrat/etat", { params });
                if (myId !== reqId.current) return;
                const list = res.data || [];
                setData(list.map((c, idx) => ({
                    key: c.codeId,
                    codeId: c.codeId,
                    idx,
                    numeroPolice: c.numeroPoliceM || c.numeroPoliceP,
                    souscripteur: c?.prenom ? `${c?.nom} ${c?.prenom}` : `${c?.nom || ""}`,
                    organisme: c?.raisonSociale,
                    codeCat: c?.codeCat,
                    immatriculation: c?.immatriculation,
                    dateEffet: c?.dateEffet ? dayjs(c.dateEffet).format("DD/MM/YYYY") : "",
                    dateEcheance: c?.dateEcheance ? dayjs(c.dateEcheance).format("DD/MM/YYYY") : "",
                    primeTotale: c?.primeTotale,
                    dateImpressionEtat: c?.dateImpressionEtat || null,
                    dateLimitePaiement: c?.dateLimitePaiement || null,
                    data: c,
                })));
                setTotal(list.length);
                return;
            }

            // ----- Onglet CONTRATS (standard) ou HORS POOL -----
            const avenant = tab?.avenant;
            const url = tabKind === "horspool" ? "/api/contrat/hors-pool" : "/api/contrat";
            const params = {
                page: page - 1,
                size: pageSize,
                ...(debouncedSearch ? { search: debouncedSearch } : {}),
                ...(avenant ? { avenant } : {}),
                ...(compagnies.length ? { compagnie: compagnies.join(",") } : {}),
                ...(dateRange ? { ...dateRange, dateField } : {}),
                // Filtre par branche (codeBranche). Le backend restreint aux contrats de cette branche.
                ...(branche ? { branche } : {}),
                // Production v3 : exclut les contrats en proposition côté backend.
                ...(exclurePropositions ? { exclurePropositions: true } : {}),
            };
            const res = await axiosInstance.get(url, { params });
            if (myId !== reqId.current) return; // ignore réponses obsolètes
            const { content = [], totalElements = 0 } = res.data || {};
            setData(content.map((c, idx) => ({
                key: c.codeId,
                codeId: c.codeId,
                idx,
                numeroPolice: c.numeroPoliceM || c.numeroPoliceP,
                souscripteur: c?.prenom ? `${c?.nom} ${c?.prenom}` : `${c?.nom || ""}`,
                organisme: c?.raisonSociale,
                agence: c?.agence,
                avenant: c?.avenant,
                codeAvenant: c?.codeAvenant,
                dateEffet: c?.dateEffet ? dayjs(c.dateEffet).format("DD/MM/YYYY - HH:mm") : "",
                dateEcheance: c?.dateEcheance ? dayjs(c.dateEcheance).format("DD/MM/YYYY - HH:mm") : "",
                // Date de souscription = date de validation (sinon updatedAt/createdAt côté backend)
                dateSouscription: c?.dateSouscription
                    ? frenchDateOnly(c.dateSouscription)
                    : (c?.dateValidation ? frenchDateOnly(c.dateValidation) : ""),
                // Réglé ⟺ la police a un règlement encaissé ET cette version n'a aucun règlement propre
                // non encaissé. Un avenant d'ordre (0 règlement propre) hérite de l'AN → réglé ; un
                // contrat sans aucun encaissement → non réglé. Repli sur l'ancien compteur tant que le
                // backend n'expose pas reglementsEncaisses (jar non redéployé).
                regle: c.reglementsEncaisses != null
                    ? (c.reglementsEncaisses > 0 && (c.reglementsNonRegles || 0) === 0)
                    : c.reglements > 0,
                primeTotale: c?.primeTotale,
                // prime_nette_fractionnee remonte à 0 (et non null) pour les contrats non
                // fractionnés (rs.getDouble) → on retombe sur la prime nette annuelle.
                primeNette: (c?.primeNetteFractionnee && c.primeNetteFractionnee !== 0)
                    ? c.primeNetteFractionnee
                    : c?.primeNette,
                accessoiresInclus: !!(c?.primeNetteFractionnee && c.primeNetteFractionnee !== 0),
                // Accessoires réconciliés avec la TTC côté backend (getter ContratSearchDTO.getAccessoires).
                accessoires: c?.accessoires,
                taxes: c?.taxes,
                // Onglet « Garanties hors Pool » uniquement (null sur les autres onglets).
                primeNetteCompagnie: c?.primeNetteCompagnie,
                garantiesHorsPool: c?.garantiesHorsPool,
                data: c,
            })));
            setTotal(totalElements);
        } catch (e) {
            // En cas d'échec (ex. endpoint non déployé → 404), on VIDE la table : sinon les lignes
            // de l'onglet précédent restent affichées et donnent l'illusion de résultats erronés.
            if (myId === reqId.current) {
                console.error("Production v2 fetch:", e);
                setData([]);
                setTotal(0);
            }
        } finally {
            if (myId === reqId.current) setLoading(false);
        }
    }, [activeTab, page, pageSize, debouncedSearch, compagnies, dateRange, dateField, exclurePropositions, branche]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Totaux/badges : recalculés selon la période + la compagnie/agence sélectionnées.
    const fetchCounts = useCallback(() => {
        const params = {
            ...(compagnies.length ? { compagnie: compagnies.join(",") } : {}),
            ...(dateRange ? { ...dateRange, dateField } : {}),
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(branche ? { branche } : {}),
            ...(exclurePropositions ? { exclurePropositions: true } : {}),
        };
        axiosInstance.get("/api/contrat/count", { params })
            .then((r) => setCount(r.data || {}))
            .catch(() => { });
    }, [compagnies, dateRange, dateField, debouncedSearch, branche, exclurePropositions]);

    useEffect(() => { fetchCounts(); }, [fetchCounts]);

    // Chargements ponctuels (listes de référence du filtre organisme)
    useEffect(() => {
        if (isPoolSiege || isCourtier) {
            axiosInstance.get("/api/organisme/assurance").then((r) => {
                setMembres((r.data || []).map((m) => ({ label: m.raisonSociale, value: m.code })));
            }).catch(() => { });
        }
        if (isPoolSiege) {
            // Agences du Pool (bureaux DTRF, STCA…) — valeur = nom (matché par le filtre courtier a.nom)
            axiosInstance.get("/api/agence/pool/subsidiaries").then((r) => {
                setPoolAgences((r.data || [])
                    .filter((a) => a?.nom)
                    .map((a) => ({ label: a.ville ? `${a.nom} (${a.ville})` : a.nom, value: a.nom })));
            }).catch(() => { });
        }
        // Compagnie au siège : agences de la compagnie (valeur = nom, matché par a.nom/ap.nom côté SQL)
        if (isCompagnieSiege && COMPAGNIE_CODE) {
            axiosInstance.get(`/api/agence/organisme/code/${COMPAGNIE_CODE}`).then((r) => {
                setCompagnieAgences((r.data || [])
                    .filter((a) => a?.nom)
                    .map((a) => ({ label: a.ville ? `${a.nom} (${a.ville})` : a.nom, value: a.nom })));
            }).catch(() => { });
        }
    }, []);

    // Options groupées du filtre organisme : compagnies + agences du Pool
    const organismeOptions = useMemo(() => {
        const groups = [];
        if (membres.length) groups.push({ label: "Compagnies", title: "Compagnies", options: membres });
        if (poolAgences.length) groups.push({ label: "Agences du Pool", title: "Agences du Pool", options: poolAgences });
        return groups;
    }, [membres, poolAgences]);

    // Libellé de la branche sélectionnée (pour les en-têtes d'export) : « NOM (CODE) ».
    const brancheLabel = useMemo(() => {
        if (!branche) return "";
        const b = branches.find((x) => x.codeBranche === branche);
        return b?.nom ? `${b.nom} (${b.codeBranche})` : branche;
    }, [branche, branches]);

    // Compagnie(s) pour l'en-tête d'export (affiché quand une branche est précisée) :
    // filtre compagnie sélectionné → noms ; sinon organisme connecté (« Toutes les
    // compagnies » pour le Pool sans filtre).
    const compagnieLabel = useMemo(() => {
        if (compagnies?.length) {
            return compagnies.map((code) => membres.find((m) => m.value === code)?.label || code).join(", ");
        }
        const org = LOGGED_USER?.profil?.organisme;
        if (org?.typeOrganisme === "POOL") return "Toutes les compagnies";
        return org?.raisonSociale || "";
    }, [compagnies, membres]);

    // Nettoie un segment de nom de fichier (accents/espaces/caractères spéciaux → _).
    const fileSafe = (s) => `${s || ""}`.normalize("NFD").replace(/[̀-ͯ]/g, "")
        .replace(/[^A-Za-z0-9-]+/g, "_").replace(/^_+|_+$/g, "");

    // Libellés d'opération (code avenant → libellé) pour la colonne « Opération/Avenant » des exports.
    const AVENANT_LIBELLES = {
        AN: "Affaire nouvelle", RN: "Renouvellement", AG: "Augmentation", DG: "Diminution",
        IN: "Incorporation", PR: "Prorogation", TG: "Transfert", RV: "Remise en vigueur",
        RT: "Retrait véhicule", SP: "Suspension", RS: "Résiliation", AD: "Duplicata",
        AO: "Avenant d'ordre", CV: "Changement de véhicule", AA: "Avenant d'annulation",
    };

    const generateExcelFileFromArray = (array, fileName = "ListingPolices", metaLines = [], horsPool = false) => {
        const headers = [
            "N° Police", "Immatriculation",
            // Colonne BRANCHE ligne par ligne UNIQUEMENT si aucune branche n'est filtrée
            // (si une branche est sélectionnée, elle est déjà indiquée dans l'en-tête du fichier).
            ...(!horsPool && !branche ? ["Branche"] : []),
            "Assuré", "Opération/Avenant", "Date effet", "Date échéance",
            "Date souscription", "Puissance/Volume", "Prime nette",
            // Répartition de la prime nette (valeurs ANNUELLES) : Pool = nette − part compagnie.
            ...(horsPool ? [] : ["Prime nette Pool", "Prime nette compagnie"]),
            "Accessoires", "Taxes", "Prime TTC",
            ...(horsPool ? ["Prime compagnie", "Garanties hors Pool"] : ["Statut règlement"]),
            "Mode de paiement", "N° quittance / Référence", "Prime à reporter", "Agence", "Proposé par", "Apporteur", "Concessionnaire",
        ];
        const fd = (d) => (d ? frenchDateOnly(d) : "");
        const fl = (v) => (v != null ? Math.floor(v) : "");
        const eng = (it) => it?.engins?.[0];
        // Aligné sur la liste : prime nette FRACTIONNÉE quand elle existe (≠ 0), sinon annuelle.
        const estFractionne = (it) => !!(it?.primeNetteFractionnee && it.primeNetteFractionnee !== 0);
        const pnEff = (it) => (estFractionne(it) ? it.primeNetteFractionnee : it?.primeNette);
        const resolvers = {
            "N° Police": (it) => it?.numeroPoliceP ?? "",
            "Immatriculation": (it) => eng(it)?.immatriculation ?? "",
            "Assuré": (it) => [it?.souscripteur?.nom, it?.souscripteur?.prenom].filter(Boolean).join(" "),
            "Date effet": (it) => fd(it?.dateEffet),
            "Date échéance": (it) => fd(it?.dateEcheance),
            "Date souscription": (it) => fd(it?.dateSouscription),
            "Puissance/Volume": (it) => eng(it)?.puissance ?? eng(it)?.volume ?? "",
            // Ventilation FACTURÉE (pnFacturee, accessoiresFactures…) calculée par le backend avec la
            // même fonction que le bordereau de cession : prime nette, coût de pièces et taxes de la
            // période payée. Repli sur les colonnes stockées si le backend ne l'envoie pas.
            "Prime nette": (it) => fl(it?.pnFacturee ?? pnEff(it)),
            "Prime nette Pool": (it) => fl(it?.pnPoolFacturee
                ?? (it?.primeNette || 0) - (it?.primeNetteCompagnie || 0)),
            "Prime nette compagnie": (it) => fl(it?.pnCompagnieFacturee ?? it?.primeNetteCompagnie),
            "Statut règlement": (it) => it?.statutReglement ?? "",
            "Accessoires": (it) => fl(it?.accessoiresFactures ?? it?.accessoires),
            "Taxes": (it) => fl(it?.taxesFacturees ?? Math.round((it?.primeTotale || 0) * 0.06 / 1.06)),
            "Prime TTC": (it) => fl(it?.primeTotale),
            // Mode(s) réellement encaissé(s) sur le mouvement (« Espèces », « Banque »…), calculé
            // côté serveur depuis les règlements encaissés. Vide ⇒ mouvement non encaissé.
            "Mode de paiement": (it) => it?.modePaiement ?? "",
            // Numéro de paiement + référence rassemblés en une seule colonne. Souvent vide sur un
            // encaissement espèces (ni quittance ni référence) — d'où la colonne « Mode » distincte.
            //
            // Un encaissement PUREMENT espèces n'a ni quittance ni référence bancaire : ce qui y
            // traîne (« P / BANQUE », « P / ESPECES ») est un résidu de reprise/synchro qui ne
            // désigne aucun justificatif réel → colonne laissée vide. Comparaison EXACTE : un
            // mouvement à modes multiples (« Espèces / Banque ») garde sa référence, qui se
            // rapporte alors au versement bancaire.
            "N° quittance / Référence": (it) => (it?.modePaiement === "Espèces"
                ? ""
                : [it?.numeroQuittance, it?.referencePaiement].filter(Boolean).join(" / ")),
            "Opération/Avenant": (it) => AVENANT_LIBELLES[it?.avenant] || it?.avenant || "",
            "Agence": (it) => it?.agence ?? "",
            "Proposé par": (it) => it?.producteur ?? "",
            "Branche": (it) => (it?.brancheNom
                ? (it?.brancheCode ? `${it.brancheNom} (${it.brancheCode})` : it.brancheNom)
                : (it?.brancheCode ?? "")),
            "Prime à reporter": (it) => fl(it?.prec),
            "Apporteur": (it) => (it?.intermediaireNom ? `${it.intermediaireNom} - ${it.intermediaireCode}` : ""),
            "Concessionnaire": (it) => it?.concessionnaire ?? "",
            "Prime compagnie": (it) => fl(it?.primeNetteCompagnie),
            "Garanties hors Pool": (it) => it?.garantiesHorsPool ?? "",
        };
        // Construction en tableau de tableaux (AOA) : évite l'allocation d'un objet par ligne
        // et la double itération de json_to_sheet → export plus rapide et moins gourmand sur gros volumes.
        const resolverFns = headers.map((h) => resolvers[h]);
        // Lignes d'en-tête (titre, période, branche…) AVANT la ligne des colonnes.
        const aoa = [...(metaLines || []).map((l) => [l]), ...(metaLines?.length ? [[]] : []), headers];
        for (const it of (array || [])) {
            aoa.push(resolverFns.map((fn) => fn(it)));
        }
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        XLSX.writeFile(wb, `${fileName}.xlsx`);
    };

    // Dataset complet (toutes lignes) pour l'onglet courant, selon la période + filtres affichés.
    const fetchExportRows = async () => {
        const tab = TABS.find((t) => t.key === activeTab);
        const kind = tab?.kind || "contrat";
        if (kind === "sinistres") {
            const res = await axiosInstance.get("/api/sinistre/all", {
                params: {
                    ...dateRange,
                    ...(compagnies.length ? { compagnie: compagnies.join(",") } : {}),
                    ...(debouncedSearch ? { search: debouncedSearch } : {}),
                },
            });
            return res?.data || [];
        }
        // Vue allégée (mêmes colonnes, JSON ~10× plus petit) : l'ancien /all renvoyait le
        // ContratDTO imbriqué complet → lent puis 500. Le rendu Excel/PDF reste côté front.
        const url = kind === "horspool" ? "/api/contrat/hors-pool/export/rows" : "/api/contrat/export/rows";
        const res = await axiosInstance.get(url, {
            params: {
                ...dateRange,
                dateField,
                ...(compagnies.length ? { compagnie: compagnies.join(",") } : {}),
                ...(kind === "horspool" ? {} : (tab?.avenant ? { avenant: tab.avenant } : {})),
                ...(debouncedSearch ? { search: debouncedSearch } : {}),
                // L'export suit les mêmes filtres que la liste : branche + exclurePropositions (v3).
                ...(kind === "horspool" ? {} : (branche ? { branche } : {})),
                ...(kind === "horspool" ? {} : (exclurePropositions ? { exclurePropositions: true } : {})),
            },
        });
        return res?.data || [];
    };

    // Export EXCEL — adapte le format selon l'onglet (contrats / hors Pool / sinistres / État).
    const handleExport = async (reglesOnly = false) => {
        // Onglet ÉTAT : export simple depuis les lignes chargées (source dédiée, non paginée).
        if (tabKind === "etat") {
            const rows = (data || []).map((r) => ({
                "N° Police": r.numeroPolice,
                "Souscripteur": r.souscripteur,
                "Compagnie": r.organisme,
                "Catégorie": r.codeCat || "",
                "Immatriculation": r.immatriculation || "",
                "Effet": r.dateEffet,
                "Échéance": r.dateEcheance,
                "Prime TTC": r.primeTotale,
                "Date d'impression": r.dateImpressionEtat ? dayjs(r.dateImpressionEtat).format("DD/MM/YYYY") : "Non imprimé",
                "Échéance de paiement (6 mois)": r.dateLimitePaiement ? dayjs(r.dateLimitePaiement).format("DD/MM/YYYY") : "",
                "Jours restants": r.dateLimitePaiement
                    ? dayjs(r.dateLimitePaiement).startOf("day").diff(dayjs().startOf("day"), "day") : "",
            }));
            if (!rows.length) { toast.warn("Aucun contrat avec l'État à exporter"); return; }
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Contrats avec l'État");
            XLSX.writeFile(wb, `Contrats_Etat_${dayjs().format("YYYY-MM-DD")}.xlsx`);
            return;
        }
        if (!dateRange) { toast.warn("Sélectionnez un exercice précis pour l'export"); return; }
        setExporting(true);
        try {
            let rows = await fetchExportRows();
            if (tabKind === "sinistres") {
                generateSinistresDeclaresExcel(rows, [dateRange.startDate, dateRange.endDate]);
            } else {
                if (reglesOnly) rows = (rows || []).filter((r) => r?.statutReglement === "Réglé");
                // Nom de fichier : base + branche (si filtrée) + période. En-tête du fichier :
                // titre + période + branche sélectionnée.
                const base = tabKind === "horspool" ? "ContratsHorsPool" : "ListingPolices";
                const fileName = [base, reglesOnly ? "regles" : null, branche ? fileSafe(branche) : null,
                    `du_${dateRange.startDate}_au_${dateRange.endDate}`].filter(Boolean).join("_");
                const metaLines = [
                    tabKind === "horspool" ? "ÉTAT DES CONTRATS AVEC GARANTIES HORS POOL" : "LISTING DES POLICES",
                    `Période : du ${frenchDateOnly(dateRange.startDate)} au ${frenchDateOnly(dateRange.endDate)}`,
                    ...(brancheLabel ? [`Branche : ${brancheLabel}`] : []),
                    // Compagnie précisée dans l'en-tête UNIQUEMENT quand une branche est sélectionnée.
                    ...(branche && compagnieLabel ? [`Compagnie : ${compagnieLabel}`] : []),
                    ...(reglesOnly ? ["Contrats réglés uniquement"] : []),
                ];
                generateExcelFileFromArray(rows, fileName, metaLines, tabKind === "horspool");
            }
        } catch (e) {
            toast.error("Erreur lors de l'export");
        } finally {
            setExporting(false);
        }
    };

    // Export BORDEREAU DE CESSION EXACT (Émissions + Annulations) — reproduit à l'identique l'écran
    // /cession en appelant la MÊME source : GET /api/contrat/organisme/month/cession/{compagnie}
    // (fonction SQL get_contrats_reglements_cession) = 1 ligne par RÈGLEMENT ENCAISSÉ du mois, avec le
    // split Pool/Compagnie proratisé côté serveur. On NE reformate PAS les contrats de production (base
    // différente) : la cession est par COMPAGNIE et par MOIS → ces deux filtres sont requis.
    // Récupère les données du BORDEREAU DE CESSION (source /cession) — PARTAGÉES par les exports Excel
    // ET PDF. Guards (compagnie unique + un seul mois) → toast + retourne null. Sinon retourne
    // { emissions, annulations, mois, moisLabel, cieLabel, cieCode }.
    const fetchBordereauCessionData = async () => {
        if (tabKind === "sinistres" || tabKind === "etat") { toast.warn("Export bordereau disponible sur les onglets contrats"); return null; }
        // Compagnie unique (la cession est par compagnie). Pool : exactement une sélectionnée ; compagnie : la sienne.
        const isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
        const cieCode = (compagnies?.length === 1) ? compagnies[0] : (!isPoolUser ? COMPAGNIE_CODE : null);
        if (!cieCode) {
            toast.warn(compagnies?.length > 1
                ? "Export bordereau (cession) : sélectionnez UNE seule compagnie."
                : "Export bordereau (cession) : sélectionnez la compagnie concernée.");
            return null;
        }
        // Un seul mois (la cession est mensuelle) → période précise dans le même mois.
        if (!customRange || !customRange[0] || !customRange[1]) {
            toast.warn("Export bordereau (cession) : choisissez une période précise (un seul mois)."); return null;
        }
        if (customRange[0].format("YYYY-MM") !== customRange[1].format("YYYY-MM")) {
            toast.warn("Export bordereau (cession) : la période doit tenir dans un seul mois."); return null;
        }
        const mois = customRange[0].format("YYYY-MM");
        // Données cession du mois pour la compagnie (toutes catégories : codeCategorie=null).
        const res = await axiosInstance.get(`/api/contrat/organisme/month/cession/${cieCode}`, {
            params: { codeCategorie: null, mois: `${mois}-01` }, timeout: 120000,
        });
        // Filtre par branche affichée : on restreint aux catégories de la branche (chaque Categorie
        // porte sa branche.codeBranche).
        let allowedCats = null;
        if (branche) {
            const catRes = await axiosInstance.get(`/api/categorie`);
            allowedCats = new Set((catRes.data || [])
                .filter((c) => c?.branche?.codeBranche === branche)
                .map((c) => c.codeCategorie));
        }
        // Aplatissement en lignes de règlement — MÊME mapping que Cession.js.
        const items = [];
        (res.data || []).forEach((contrat) => {
            if (allowedCats && !allowedCats.has(contrat.categorieCode)) return;
            (contrat.reglements || []).forEach((reg) => {
                items.push({
                    numeroPolice: contrat.numeroPoliceP,
                    avenant: reg.avenantCode || "N/A",
                    assure: contrat.souscripteurPrenom
                        ? `${contrat.souscripteurNom} ${contrat.souscripteurPrenom}`
                        : `${contrat.souscripteurNom || ""}`,
                    immatriculation: contrat.immatriculation,
                    dateEmission: contrat.dateEmission,
                    agence: contrat.agence,
                    intermediaire: contrat.intermediaire,
                    effet: contrat.dateEffet,
                    echeance: contrat.dateEcheance,
                    categorie: contrat.categorieCode,
                    primeNettePool: reg.primeNettePool,
                    primeNetteCompagnie: reg.primeNetteCompagnie,
                    accessoires: reg.accessoires,
                    taxes: reg.taxes,
                    primeACeder: reg.montant,
                });
            });
        });
        if (!items.length) { toast.info("Aucun règlement cédé pour cette compagnie / ce mois."); return null; }
        const cieLabel = (compagnies?.length === 1
            ? (membres.find((m) => m.value === cieCode)?.label)
            : LOGGED_USER?.profil?.organisme?.raisonSociale) || cieCode;
        return {
            cieCode, mois, cieLabel,
            moisLabel: moisPeriodeLabel(mois).toUpperCase(),
            emissions: items.filter((it) => (it.primeACeder || 0) >= 0),
            annulations: items.filter((it) => (it.primeACeder || 0) < 0),
        };
    };

    const handleExportBordereauCession = async () => {
        setExporting(true);
        try {
            const d = await fetchBordereauCessionData();
            if (!d) return;
            const { emissions, annulations, mois, moisLabel, cieLabel, cieCode } = d;

            // Feuilles au FORMAT EXACT de BordereauEmissions / BordereauAnnulations (mêmes colonnes,
            // trunc + abs, ligne TOTAL, dernière colonne « Prime TTC » = montant encaissé).
            const fd = (dd) => (dd ? new Date(dd).toLocaleDateString("fr-FR") : "");
            const meta = [`Mois : ${moisPeriodeLabel(mois)}`, `Compagnie : ${cieLabel}`,
            ...(brancheLabel ? [`Branche : ${brancheLabel}`] : [])];
            const tr = (v) => Math.trunc(Math.abs(v || 0));
            const som = (list, champ) => list.reduce((s, it) => s + Math.abs(it[champ] || 0), 0);

            const sheetEmissions = () => {
                const headers = ["N° Police", "Avenant", "Assuré", "Immatriculation", "Date émission",
                    "Point de vente", "Intermédiaire", "Effet", "Échéance", "Catégorie",
                    "PN Pool", "PN Compagnie", "Accessoires", "Taxes", "Prime TTC"];
                const rows = emissions.map((it) => [
                    it.numeroPolice, it.avenant, it.assure, it.immatriculation || "",
                    fd(it.dateEmission), it.agence || "", it.intermediaire || "",
                    fd(it.effet), fd(it.echeance), it.categorie,
                    tr(it.primeNettePool), tr(it.primeNetteCompagnie), tr(it.accessoires), tr(it.taxes), tr(it.primeACeder),
                ]);
                const total = ["TOTAL", "", "", "", "", "", "", "", "", "",
                    Math.trunc(som(emissions, "primeNettePool")), Math.trunc(som(emissions, "primeNetteCompagnie")),
                    Math.trunc(som(emissions, "accessoires")), Math.trunc(som(emissions, "taxes")),
                    Math.trunc(som(emissions, "primeACeder"))];
                const aoa = [[`BORDEREAU DES EMISSIONS DU MOIS DE ${moisLabel}`], ...meta.map((l) => [l]), [], headers, ...rows];
                if (rows.length) aoa.push(total);
                return XLSX.utils.aoa_to_sheet(aoa);
            };
            const sheetAnnulations = () => {
                const headers = ["N° Police", "Avenant", "Assuré", "Effet", "Échéance", "Catégorie",
                    "PN Pool", "PN Compagnie", "Accessoires", "Taxes", "Prime TTC"];
                const rows = annulations.map((it) => [
                    it.numeroPolice, it.avenant, it.assure, fd(it.effet), fd(it.echeance), it.categorie,
                    tr(it.primeNettePool), tr(it.primeNetteCompagnie), tr(it.accessoires), tr(it.taxes), tr(it.primeACeder),
                ]);
                const total = ["TOTAL", "", "", "", "", "",
                    Math.trunc(som(annulations, "primeNettePool")), Math.trunc(som(annulations, "primeNetteCompagnie")),
                    Math.trunc(som(annulations, "accessoires")), Math.trunc(som(annulations, "taxes")),
                    Math.trunc(som(annulations, "primeACeder"))];
                const aoa = [[`BORDEREAU DES ANNULATIONS DU MOIS DE ${moisLabel}`], ...meta.map((l) => [l]), [], headers, ...rows];
                if (rows.length) aoa.push(total);
                return XLSX.utils.aoa_to_sheet(aoa);
            };

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, sheetEmissions(), "Émissions");
            XLSX.utils.book_append_sheet(wb, sheetAnnulations(), "Annulations");
            const fileName = ["BordereauCession", fileSafe(cieCode), branche ? fileSafe(branche) : null, mois]
                .filter(Boolean).join("_");
            XLSX.writeFile(wb, `${fileName}.xlsx`);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Erreur lors de l'export du bordereau (cession)");
        } finally {
            setExporting(false);
        }
    };

    // Export PDF — MÊME requête/données que l'export Excel (listing production via fetchExportRows).
    // reglesOnly = n'exporter que les contrats au statut « Réglé ».
    const handleExportPdf = async (reglesOnly = false) => {
        if (tabKind === "etat") { toast.info("Onglet État : utilisez l'export Excel."); return; }
        if (!dateRange) { toast.warn("Sélectionnez un exercice précis pour l'export"); return; }
        setExportingPdf(true);
        try {
            let rows = await fetchExportRows();
            if (tabKind === "sinistres") {
                const uri = await generateSinistresDeclaresPDF(rows, "", [dateRange.startDate, dateRange.endDate]);
                const a = document.createElement("a");
                a.href = uri;
                a.download = `EtatSinistres_${dateRange.startDate}_${dateRange.endDate}.pdf`;
                a.click();
                return;
            }
            if (reglesOnly) rows = (rows || []).filter((r) => r?.statutReglement === "Réglé");
            const isHP = tabKind === "horspool";
            await generateContratsProductionPDF(rows, {
                title: isHP ? "ÉTAT DES CONTRATS AVEC GARANTIES HORS POOL" : "ÉTAT PÉRIODIQUE DES CONTRATS",
                periode: [dateRange.startDate, dateRange.endDate],
                subtitle: [
                    brancheLabel ? `Branche : ${brancheLabel}` : "",
                    (branche && compagnieLabel) ? `Compagnie : ${compagnieLabel}` : "",
                    reglesOnly ? "Contrats réglés uniquement" : "",
                ].filter(Boolean).join("   —   "),
                horsPool: isHP,
                fileName: [isHP ? "ContratsHorsPool" : "EtatProduction",
                    reglesOnly ? "regles" : null, branche ? fileSafe(branche) : null].filter(Boolean).join("_"),
            });
        } catch (e) {
            toast.error("Erreur lors de l'export PDF");
        } finally {
            setExportingPdf(false);
        }
    };

    const columns = useMemo(() => [
        {
            title: "N° Police",
            dataIndex: "numeroPolice",
            key: "numeroPolice",
            render: (val) => <Text strong style={{ color: COLORS.secondary }}>{val}</Text>,
        },
        { title: "Souscripteur", dataIndex: "souscripteur", key: "souscripteur" },
        {
            title: "Organisme / Agence",
            key: "organismeAgence",
            render: (_, r) => (
                <Space direction="vertical" size={2}>
                    <Tag color="geekblue" style={{ borderRadius: 12 }}>{r.organisme}</Tag>
                    {r.agence
                        ? <Tag color="cyan" style={{ borderRadius: 12, fontSize: 11 }}>{r.agence}</Tag>
                        : <Text type="secondary" style={{ fontSize: 11 }}>— agence —</Text>}
                </Space>
            ),
        },
        {
            title: "Avenant",
            dataIndex: "avenant",
            key: "avenant",
            align: "center",
            render: (val, r) => r.codeAvenant || val
                ? (
                    <Tooltip title={val || ""}>
                        <Tag color={AVENANT_COLORS[r.codeAvenant] || "default"} style={{ borderRadius: 12, fontWeight: 600 }}>
                            {r.codeAvenant || val}
                        </Tag>
                    </Tooltip>
                )
                : <Text type="secondary">—</Text>,
        },
        {
            title: "Effet → Échéance",
            key: "periode",
            width: 130,
            render: (_, r) => (
                <div style={{ lineHeight: 1.35, whiteSpace: "nowrap" }}>
                    <Text style={{ fontSize: 12.5 }}>{r.dateEffet || "—"}</Text>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        <RightOutlined style={{ fontSize: 8, marginRight: 4 }} />{r.dateEcheance || "—"}
                    </div>
                </div>
            ),
        },
        {
            title: "Souscription",
            dataIndex: "dateSouscription",
            key: "dateSouscription",
            align: "center",
            render: (val) => val
                ? <Text style={{ fontSize: 12 }}>{val}</Text>
                : <Text type="secondary">—</Text>,
        },
        {
            title: "Statut",
            dataIndex: "regle",
            key: "regle",
            align: "center",
            filters: [{ text: "Réglé", value: true }, { text: "Non réglé", value: false }],
            onFilter: (v, r) => r.regle === v,
            render: (regle) => (
                <Tag color={regle ? "success" : "warning"} style={{ borderRadius: 12, fontWeight: 600 }}>
                    {regle ? "RÉGLÉ" : "NON RÉGLÉ"}
                </Tag>
            ),
        },
        {
            title: "Primes (FCFA)",
            align: "center",
            children: [
                {
                    title: "Prime nette", dataIndex: "primeNette", key: "primeNette", align: "right",
                    onCell: () => ({ style: { whiteSpace: "nowrap" } }),
                    render: (v) => <Text style={{ whiteSpace: "nowrap" }}>{displayNumbers(v)}</Text>,
                },
                {
                    title: "Accessoires", dataIndex: "accessoires", key: "accessoires", align: "right",
                    onCell: () => ({ style: { whiteSpace: "nowrap" } }),
                    render: (v) => <Text type="secondary" style={{ whiteSpace: "nowrap" }}>{displayNumbers(v)}</Text>,
                },
                {
                    title: "Taxes", dataIndex: "taxes", key: "taxes", align: "right",
                    onCell: () => ({ style: { whiteSpace: "nowrap" } }),
                    render: (v) => <Text type="secondary" style={{ whiteSpace: "nowrap" }}>{displayNumbers(v)}</Text>,
                },
                {
                    title: "Prime TTC", dataIndex: "primeTotale", key: "primeTotale", align: "right",
                    onCell: () => ({ style: { whiteSpace: "nowrap" } }),
                    render: (v) => <Text strong style={{ color: COLORS.secondary, whiteSpace: "nowrap" }}>{displayNumbers(v)}</Text>,
                },
            ],
        },
        ...(tabKind === "horspool" ? [
            {
                // Répartition annuelle (comme l'export Excel) : PN Pool = prime nette annuelle − part compagnie.
                title: "Prime nette Pool", key: "primeNettePool", align: "right", width: 130,
                onCell: () => ({ style: { whiteSpace: "nowrap" } }),
                render: (_, r) => (
                    <Text strong style={{ color: COLORS.secondary, whiteSpace: "nowrap" }}>
                        {displayNumbers((r.data?.primeNette || 0) - (r.primeNetteCompagnie || 0))}
                    </Text>
                ),
            },
            {
                title: "Prime nette compagnie", dataIndex: "primeNetteCompagnie", key: "primeNetteCompagnie", align: "right", width: 130,
                onCell: () => ({ style: { whiteSpace: "nowrap" } }),
                render: (v) => (Number(v) > 0
                    ? <Text strong style={{ color: "#7c3aed", whiteSpace: "nowrap" }}>{displayNumbers(v)}</Text>
                    : <Text type="secondary" style={{ whiteSpace: "nowrap" }}>—</Text>),
            },
            {
                title: "Garanties hors Pool", dataIndex: "garantiesHorsPool", key: "garantiesHorsPool", width: 220, ellipsis: true,
                render: (v) => v
                    ? <Tooltip title={v}><Tag color="purple" style={{ borderRadius: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{v}</Tag></Tooltip>
                    : <Text type="secondary">—</Text>,
            },
        ] : []),
        {
            title: "", key: "action", align: "center", width: 60,
            render: (_, r) => (
                <Tooltip title="Détails">
                    <Button type="text" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); navigate(`/details-contrat/${r.codeId}`); }} />
                </Tooltip>
            ),
        },
    ], [navigate, tabKind]);

    // Colonnes de l'onglet « Contrats avec l'État » (source /api/contrat/etat) : ajoute la colonne
    // « Délai de paiement » = compte à rebours entre l'impression et l'échéance (impression + 6 mois).
    const etatColumns = useMemo(() => [
        {
            title: "N° Police", dataIndex: "numeroPolice", key: "numeroPolice",
            render: (val) => <Text strong style={{ color: COLORS.secondary }}>{val}</Text>,
        },
        { title: "Souscripteur", dataIndex: "souscripteur", key: "souscripteur" },
        {
            title: "Compagnie", dataIndex: "organisme", key: "organisme",
            render: (v) => <Tag color="geekblue" style={{ borderRadius: 12 }}>{v}</Tag>,
        },
        { title: "Cat.", dataIndex: "codeCat", key: "codeCat", align: "center", width: 70,
            render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text> },
        { title: "Immatriculation", dataIndex: "immatriculation", key: "immatriculation" },
        {
            title: "Effet → Échéance", key: "periode", width: 130,
            render: (_, r) => (
                <div style={{ lineHeight: 1.35, whiteSpace: "nowrap" }}>
                    <Text style={{ fontSize: 12.5 }}>{r.dateEffet || "—"}</Text>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        <RightOutlined style={{ fontSize: 8, marginRight: 4 }} />{r.dateEcheance || "—"}
                    </div>
                </div>
            ),
        },
        {
            title: "Prime TTC", dataIndex: "primeTotale", key: "primeTotale", align: "right",
            onCell: () => ({ style: { whiteSpace: "nowrap" } }),
            render: (v) => <Text strong style={{ color: COLORS.secondary, whiteSpace: "nowrap" }}>{displayNumbers(v)}</Text>,
        },
        {
            title: "Délai de paiement", key: "delai", align: "center", width: 170,
            render: (_, r) => {
                if (!r.dateLimitePaiement) {
                    return <Tag style={{ borderRadius: 12 }}>Non imprimé</Tag>;
                }
                const jours = dayjs(r.dateLimitePaiement).startOf("day").diff(dayjs().startOf("day"), "day");
                const color = jours < 0 ? "error" : (jours <= 30 ? "warning" : "success");
                const label = jours < 0 ? `En retard (${Math.abs(jours)} j)` : `${jours} j restant(s)`;
                return (
                    <Tooltip title={`Impression : ${r.dateImpressionEtat ? dayjs(r.dateImpressionEtat).format("DD/MM/YYYY") : "—"} · Échéance : ${dayjs(r.dateLimitePaiement).format("DD/MM/YYYY")}`}>
                        <Tag color={color} style={{ borderRadius: 12, fontWeight: 600 }}>{label}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "", key: "action", align: "center", width: 60,
            render: (_, r) => (
                <Tooltip title="Détails">
                    <Button type="text" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); navigate(`/details-contrat/${r.codeId}`); }} />
                </Tooltip>
            ),
        },
    ], [navigate]);

    // Colonnes de l'onglet SINISTRES (jeu de données /api/sinistre/sql).
    const sinistreColumns = useMemo(() => [
        {
            title: "N° Sinistre", dataIndex: "numeroSinistre", key: "numeroSinistre",
            render: (val) => <Text strong style={{ color: COLORS.secondary }}>{val || "—"}</Text>,
        },
        { title: "N° Police", dataIndex: "numeroPolice", key: "numeroPolice" },
        { title: "Assuré", dataIndex: "souscripteur", key: "souscripteur" },
        {
            title: "Organisme / Agence",
            key: "organismeAgence",
            render: (_, r) => (
                <Space direction="vertical" size={2}>
                    {r.organisme ? <Tag color="geekblue" style={{ borderRadius: 12 }}>{r.organisme}</Tag> : <Text type="secondary">—</Text>}
                    {r.agence
                        ? <Tag color="cyan" style={{ borderRadius: 12, fontSize: 11 }}>{r.agence}</Tag>
                        : <Text type="secondary" style={{ fontSize: 11 }}>— agence —</Text>}
                </Space>
            ),
        },
        { title: "Immatriculation", dataIndex: "immatriculation", key: "immatriculation", render: (v) => v || "—" },
        {
            title: "Survenance", dataIndex: "dateSinistre", key: "dateSinistre", align: "center",
            render: (v) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text>,
        },
        {
            title: "Déclaration", dataIndex: "dateDeclaration", key: "dateDeclaration", align: "center",
            render: (v) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text>,
        },
        {
            title: "État", dataIndex: "etat", key: "etat", align: "center",
            render: (etat) => {
                const cfg = SINISTRE_ETAT[etat] || { text: "—", color: "default" };
                return <Tag color={cfg.color} style={{ borderRadius: 12, fontWeight: 600 }}>{cfg.text}</Tag>;
            },
        },
        {
            title: "Évaluation (FCFA)", dataIndex: "evaluation", key: "evaluation", align: "right",
            onCell: () => ({ style: { whiteSpace: "nowrap" } }),
            render: (v) => <Text strong style={{ color: COLORS.secondary, whiteSpace: "nowrap" }}>{displayNumbers(v || 0)}</Text>,
        },
        {
            title: "", key: "action", align: "center", width: 60,
            render: (_, r) => (
                <Tooltip title="Détails">
                    <Button type="text" icon={<EyeOutlined />}
                        onClick={(e) => { e.stopPropagation(); navigate("/details-sinistre", { state: { codeId: r.codeId } }); }} />
                </Tooltip>
            ),
        },
    ], [navigate]);

    // Colonnes effectives selon l'onglet courant.
    const tableColumns = tabKind === "sinistres" ? sinistreColumns
        : (tabKind === "etat" ? etatColumns : columns);

    const tabItems = TABS.map((t) => ({
        key: t.key,
        label: (
            <Space size={6}>
                {t.icon}
                {t.label}
                {t.countKey != null && count?.[t.countKey] != null && (
                    <Badge count={count[t.countKey]} overflowCount={9999} style={{ backgroundColor: t.color }} />
                )}
            </Space>
        ),
    }));

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PRODUCTION"} />
                <div className="layout-page">
                    <NavBar
                        filteredResults={searchResults}
                        setFilteredResults={setSearchResults}
                        renderSearchResults={renderSearchResults}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                    />
                    <div className="content-wrapper">
                        {/* Pleine largeur : le container-xxl bride à ~1320px et gaspille les
                            marges latérales sur grand écran → le tableau large scrollait. */}
                        <div className="container-xxl flex-grow-1 container-p-y" style={{ maxWidth: "100%", paddingLeft: 16, paddingRight: 16 }}>

                            {/* En-tête */}
                            <div style={{
                                background: COLORS.gradient, borderRadius: 16, padding: "24px 28px",
                                marginBottom: 20, color: "#fff", boxShadow: "0 10px 24px rgba(36,33,102,0.25)",
                                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16,
                            }}>
                                <div>
                                    <Title level={2} style={{ color: "#fff", margin: 0 }}>Production</Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Portefeuille des contrats — version optimisée
                                        <Tag style={{ marginLeft: 8, borderRadius: 10 }} color="gold">Aperçu v2</Tag>
                                    </Text>
                                </div>
                                <Space wrap>
                                    {/* Export Excel — clic = tous ; menu déroulant = réglés uniquement. */}
                                    <Dropdown.Button size="large" loading={exporting} onClick={() => handleExport(false)}
                                        icon={<DownOutlined />}
                                        menu={{
                                            items: [{ key: "regles", label: "Réglés uniquement" }],
                                            onClick: ({ key }) => { if (key === "regles") handleExport(true); },
                                        }}
                                        style={{ borderRadius: 10 }}>
                                        <DownloadOutlined /> Exporter Excel
                                    </Dropdown.Button>
                                    {/* Export PDF — MÊME requête que l'Excel (listing production) ; menu = réglés uniquement. */}
                                    <Dropdown.Button size="large" loading={exportingPdf} onClick={() => handleExportPdf(false)}
                                        icon={<DownOutlined />}
                                        menu={{
                                            items: [{ key: "regles", label: "Réglés uniquement" }],
                                            onClick: ({ key }) => { if (key === "regles") handleExportPdf(true); },
                                        }}
                                        style={{ borderRadius: 10 }}>
                                        <FilePdfOutlined /> Exporter PDF
                                    </Dropdown.Button>
                                    {tabKind !== "sinistres" && (
                                        <Button size="large" icon={<PlusOutlined />} onClick={() => navigate("/ajouter-contrat-new")}
                                            style={{ background: "#fff", color: COLORS.secondary, border: "none", borderRadius: 10, height: 44, fontWeight: 600 }}>
                                            Nouveau contrat
                                        </Button>
                                    )}
                                </Space>
                            </div>

                            {/* Barre de filtres */}
                            <Card bordered={false} style={{ borderRadius: 16, marginBottom: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    {/* Navigateur d'exercice */}
                                    <Col xs={24} md={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Exercice</Text>
                                        <Space.Compact style={{ width: "100%" }}>
                                            <Button icon={<LeftOutlined />} disabled={!!customRange || exercice === "ALL" || exercice <= EXERCICES[0]}
                                                onClick={() => { setExercice((y) => (y === "ALL" ? CURRENT_YEAR : y - 1)); setPage(1); }} />
                                            <Select
                                                style={{ flex: 1 }}
                                                value={exercice}
                                                disabled={!!customRange}
                                                onChange={(v) => { setExercice(v); setPage(1); }}
                                                suffixIcon={<CalendarOutlined />}
                                                options={[
                                                    { label: "Tous les exercices", value: "ALL" },
                                                    ...EXERCICES.slice().reverse().map((y) => ({ label: `Exercice ${y}`, value: y })),
                                                ]}
                                            />
                                            <Button icon={<RightOutlined />} disabled={!!customRange || exercice === "ALL" || exercice >= EXERCICES[EXERCICES.length - 1]}
                                                onClick={() => { setExercice((y) => (y === "ALL" ? CURRENT_YEAR : y + 1)); setPage(1); }} />
                                        </Space.Compact>
                                    </Col>

                                    {/* Période précise (prime sur l'exercice) */}
                                    <Col xs={24} md={8}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Période précise</Text>
                                            <Select
                                                size="small"
                                                variant="borderless"
                                                value={dateField}
                                                onChange={(v) => { setDateField(v); setPage(1); }}
                                                popupMatchSelectWidth={false}
                                                style={{ fontSize: 12 }}
                                                options={[
                                                    // Fusion création + mise à jour : un contrat entre dans la période
                                                    // s'il a été CRÉÉ ou MIS À JOUR dans l'intervalle. Filtre par défaut.
                                                    { label: "selon Date de souscription", value: "created_or_updated" },
                                                    { label: "selon Date d'effet", value: "date_effet" },
                                                ]}
                                            />
                                        </div>
                                        <RangePicker
                                            style={{ width: "100%" }}
                                            format="DD/MM/YYYY"
                                            allowClear
                                            value={customRange}
                                            placeholder={["Date début", "Date fin"]}
                                            onChange={(vals) => { setCustomRange(vals); setPage(1); }}
                                        />
                                    </Col>

                                    {/* Branche (catégorie) : compagnies ne voient pas les branches Pool (VT/GUM) */}
                                    <Col xs={24} md={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Branche</Text>
                                        <Select allowClear showSearch style={{ width: "100%" }}
                                            placeholder="Toutes les branches" optionFilterProp="label"
                                            value={branche || undefined}
                                            onChange={(v) => { setBranche(v || null); setPage(1); }}
                                            options={branches.map((b) => ({
                                                value: b.codeBranche,
                                                label: b.nom ? `${b.nom} (${b.codeBranche})` : b.codeBranche,
                                            }))} />
                                    </Col>

                                    {/* Organisme : compagnies + agences du Pool (POOL siège) */}
                                    {isPoolSiege && (
                                        <Col xs={24} md={8}>
                                            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Organisme</Text>
                                            <Select mode="multiple" allowClear style={{ width: "100%" }}
                                                placeholder="Compagnie / Agence du Pool" maxTagCount="responsive"
                                                suffixIcon={<BankOutlined />}
                                                value={compagnies} onChange={(v) => { setCompagnies(v); setPage(1); }}
                                                optionFilterProp="label"
                                                options={organismeOptions} />
                                        </Col>
                                    )}

                                    {/* Agence (compagnie au siège) : filtre par agence émettrice */}
                                    {isCompagnieSiege && (
                                        <Col xs={24} md={8}>
                                            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Agence</Text>
                                            <Select mode="multiple" allowClear style={{ width: "100%" }}
                                                placeholder="Filtrer par agence émettrice" maxTagCount="responsive"
                                                suffixIcon={<BankOutlined />}
                                                value={compagnies} onChange={(v) => { setCompagnies(v); setPage(1); }}
                                                optionFilterProp="label"
                                                options={compagnieAgences} />
                                        </Col>
                                    )}

                                    {/* Compagnie (courtier) : filtre ses contrats par compagnie */}
                                    {isCourtier && (
                                        <Col xs={24} md={8}>
                                            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Compagnie</Text>
                                            <Select mode="multiple" allowClear style={{ width: "100%" }}
                                                placeholder="Filtrer par compagnie" maxTagCount="responsive"
                                                suffixIcon={<BankOutlined />}
                                                value={compagnies} onChange={(v) => { setCompagnies(v); setPage(1); }}
                                                optionFilterProp="label"
                                                options={membres} />
                                        </Col>
                                    )}

                                    {/* Recherche (pleine largeur) */}
                                    <Col xs={24}>
                                        <Input allowClear size="large" prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                                            placeholder="Rechercher (police, souscripteur, immatriculation…)"
                                            value={search} onChange={(e) => setSearch(e.target.value)} />
                                    </Col>
                                </Row>
                            </Card>

                            {/* Statistiques rapides */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                                <Col xs={12} md={6}>
                                    <StatCard title="Affaires nouvelles" value={count?.avenantANCount ?? "—"} subtitle="Exercice"
                                        icon={<FileAddOutlined style={{ color: "#fff", fontSize: 20 }} />} iconBg="linear-gradient(135deg,#52c41a 0%,#242166 100%)" />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Renouvellements" value={count?.avenantRNCount ?? "—"} subtitle="Exercice"
                                        icon={<ReloadOutlined style={{ color: "#fff", fontSize: 20 }} />} iconBg="linear-gradient(135deg,#1890ff 0%,#242166 100%)" />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Suspensions" value={count?.avenantSPCount ?? "—"} subtitle="Exercice"
                                        icon={<PauseCircleOutlined style={{ color: "#fff", fontSize: 20 }} />} iconBg="linear-gradient(135deg,#faad14 0%,#242166 100%)" />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Total affiché" value={total} subtitle="Résultats filtrés"
                                        icon={<AppstoreOutlined style={{ color: "#fff", fontSize: 20 }} />} iconBg={COLORS.gradient} />
                                </Col>
                            </Row>

                            {/* Tableau */}
                            <Card bordered={false} style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: "8px 8px 0" }}>
                                <Tabs activeKey={activeTab} items={tabItems} onChange={(k) => { setActiveTab(k); setPage(1); }} style={{ padding: "0 12px" }} />
                                <Table
                                    columns={tableColumns}
                                    dataSource={data}
                                    loading={loading}
                                    rowKey="key"
                                    onRow={(r) => ({
                                        style: { cursor: "pointer" },
                                        onClick: () => (tabKind === "sinistres"
                                            ? navigate("/details-sinistre", { state: { codeId: r.codeId } })
                                            : navigate(`/details-contrat/${r.codeId}`)),
                                    })}
                                    pagination={{
                                        current: page,
                                        pageSize,
                                        total,
                                        showSizeChanger: true,
                                        showTotal: (t) => `${t} ${tabKind === "sinistres" ? "sinistre(s)" : "contrat(s)"}`,
                                        onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                                    }}
                                    scroll={{ x: tabKind === "horspool" ? 1750 : 1400 }}
                                />
                            </Card>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductionV2;
